import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    Users, 
    Check, 
    Crown,
    Sparkles,
    Calendar,
    Video,
    MessageSquare,
    Phone,
    Star,
    Plane,
    Gift,
    UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Square Payment Links
const PAYMENT_LINKS = {
    basic_monthly: 'https://square.link/u/isfZ3krF',
    basic_yearly: 'https://square.link/u/qfY0FGS6',
    premier_monthly: 'https://square.link/u/ZhJEHj8D',
    premier_yearly: 'https://square.link/u/nqYicfQq',
    vip_monthly: 'https://square.link/u/WSsS0C7x',
    vip_yearly: 'https://square.link/u/lqonzjef',
};

export default function Membership() {
    const { user } = useAuth();
    const [billingCycle, setBillingCycle] = useState('monthly');

    // Calculate yearly prices with 10% discount
    const calculateYearlyPrice = (monthlyPrice) => {
        const yearlyTotal = monthlyPrice * 12;
        const discounted = yearlyTotal * 0.9; // 10% off
        return discounted.toFixed(2);
    };

    const handlePurchase = (tier, cycle) => {
        const linkKey = `${tier}_${cycle}`;
        const paymentUrl = PAYMENT_LINKS[linkKey];
        if (paymentUrl) {
            window.open(paymentUrl, '_blank');
        } else {
            toast.error('Payment link not available');
        }
    };

    // Features for each tier
    const basicFeatures = [
        'Live Group Coaching Sessions (4x/month) - 60 min "Study Hall"',
        'Weekly Live Q&A with Coach Shelly',
        'Community Access & Live Chat',
        'Mock Interview Practice on Live Platform',
        'STAR-Method & Body Language Feedback',
        'Hot-Seat Format + Group Q&A',
    ];

    const premierFeatures = [
        ...basicFeatures,
        'Airlines Direct Links Database (Updated Bi-Weekly)',
        'Personalized Advice on What You\'re Doing Wrong',
    ];

    const vipFeatures = [
        ...premierFeatures,
        'FREE 30-Min One-on-One Coaching Monthly',
        'Monthly Live Q&A with Guest Speakers (FA\'s & Recruiters)',
        '20% OFF All 1:1 Private Mock Interview Sessions',
        'Ongoing Support & Mentorship from Coach Shelly',
    ];

    const tiers = [
        {
            id: 'basic',
            name: 'Basic',
            monthlyPrice: 29,
            yearlyPrice: calculateYearlyPrice(29),
            description: 'Perfect for getting started',
            icon: Users,
            iconBg: 'bg-stone-500/10',
            iconColor: 'text-stone-600',
            features: basicFeatures,
            popular: false,
            buttonVariant: 'outline',
            borderClass: 'border-stone-200',
        },
        {
            id: 'premier',
            name: 'Premier',
            monthlyPrice: 49,
            yearlyPrice: calculateYearlyPrice(49),
            description: 'Most value for serious students',
            icon: Crown,
            iconBg: 'bg-yellow-500/10',
            iconColor: 'text-yellow-600',
            features: premierFeatures,
            popular: true,
            buttonVariant: 'default',
            borderClass: 'border-yellow-500 border-2',
        },
        {
            id: 'vip',
            name: 'VIP',
            monthlyPrice: 99,
            yearlyPrice: calculateYearlyPrice(99),
            description: 'Ultimate support & mentorship',
            icon: Sparkles,
            iconBg: 'bg-purple-500/10',
            iconColor: 'text-purple-600',
            features: vipFeatures,
            popular: false,
            buttonVariant: 'outline',
            borderClass: 'border-purple-200',
        },
    ];

    return (
        <div className="min-h-screen py-12 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div 
                    className="text-center mb-12"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <Badge className="mb-4 bg-primary/10 text-primary">Membership Plans</Badge>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                        Join the Flight Attendant Academy
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
                        Get access to live coaching, community support, and exclusive resources to help you land your dream job
                    </p>

                    {/* Billing Toggle */}
                    <div className="flex items-center justify-center gap-4 mb-8">
                        <Tabs value={billingCycle} onValueChange={setBillingCycle} className="w-auto">
                            <TabsList className="grid w-[300px] grid-cols-2">
                                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                                <TabsTrigger value="yearly" className="relative">
                                    Yearly
                                    <Badge className="absolute -top-3 -right-3 bg-green-500 text-white text-[10px] px-1.5">
                                        Save 10%
                                    </Badge>
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>
                </motion.div>

                {/* Pricing Cards */}
                <div className="grid md:grid-cols-3 gap-6 mb-12">
                    {tiers.map((tier, index) => {
                        const Icon = tier.icon;
                        const price = billingCycle === 'monthly' ? tier.monthlyPrice : tier.yearlyPrice;
                        const isYearly = billingCycle === 'yearly';
                        
                        return (
                            <motion.div
                                key={tier.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card className={`p-6 h-full flex flex-col relative ${tier.borderClass} ${tier.popular ? 'shadow-lg' : ''}`}>
                                    {tier.popular && (
                                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-500 text-white">
                                            Most Popular
                                        </Badge>
                                    )}
                                    
                                    {/* Header */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className={`h-12 w-12 rounded-xl ${tier.iconBg} flex items-center justify-center`}>
                                            <Icon className={`h-6 w-6 ${tier.iconColor}`} />
                                        </div>
                                        <div>
                                            <h3 className="font-heading text-xl font-semibold">{tier.name}</h3>
                                            <p className="text-sm text-muted-foreground">{tier.description}</p>
                                        </div>
                                    </div>

                                    {/* Price */}
                                    <div className="mb-6">
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl font-bold text-primary">${price}</span>
                                            <span className="text-muted-foreground">
                                                {isYearly ? '/year' : '/month'}
                                            </span>
                                        </div>
                                        {isYearly && (
                                            <p className="text-sm text-green-600 mt-1">
                                                Save ${(tier.monthlyPrice * 12 * 0.1).toFixed(2)} per year
                                            </p>
                                        )}
                                    </div>

                                    {/* Features */}
                                    <div className="flex-1 mb-6">
                                        <p className="text-sm font-semibold mb-3">What's included:</p>
                                        <ul className="space-y-2">
                                            {tier.features.map((feature, i) => {
                                                const isNew = feature.includes('NEW') || 
                                                    (tier.id === 'premier' && i >= basicFeatures.length) ||
                                                    (tier.id === 'vip' && i >= premierFeatures.length - 2);
                                                return (
                                                    <li key={i} className="flex items-start gap-2 text-sm">
                                                        <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                                                        <span>
                                                            {feature}
                                                            {isNew && tier.id !== 'basic' && i >= basicFeatures.length && (
                                                                <Badge className="ml-2 bg-blue-500 text-white text-[10px] px-1">NEW</Badge>
                                                            )}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>

                                    {/* CTA Button */}
                                    <Button 
                                        className={`w-full rounded-full ${tier.popular ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : ''}`}
                                        variant={tier.buttonVariant}
                                        onClick={() => handlePurchase(tier.id, billingCycle)}
                                        data-testid={`purchase-${tier.id}-btn`}
                                    >
                                        Get {tier.name} Access
                                    </Button>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>

                {/* What You Get Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    <Card className="p-8 bg-gradient-to-br from-stone-50 to-stone-100 dark:from-stone-900 dark:to-stone-800">
                        <h2 className="font-heading text-2xl font-bold text-center mb-8">
                            What Every Member Gets
                        </h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="text-center">
                                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                    <Video className="h-8 w-8 text-primary" />
                                </div>
                                <h3 className="font-semibold mb-2">Live Coaching Sessions</h3>
                                <p className="text-sm text-muted-foreground">
                                    Join 4 live group sessions per month with Coach Shelly
                                </p>
                            </div>
                            <div className="text-center">
                                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                    <MessageSquare className="h-8 w-8 text-primary" />
                                </div>
                                <h3 className="font-semibold mb-2">Community Access</h3>
                                <p className="text-sm text-muted-foreground">
                                    Connect with fellow aspiring flight attendants
                                </p>
                            </div>
                            <div className="text-center">
                                <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                    <UserCheck className="h-8 w-8 text-primary" />
                                </div>
                                <h3 className="font-semibold mb-2">Mock Interviews</h3>
                                <p className="text-sm text-muted-foreground">
                                    Practice with real interview scenarios and get feedback
                                </p>
                            </div>
                        </div>
                    </Card>
                </motion.div>

                {/* FAQ or Guarantee */}
                <motion.div 
                    className="mt-12 text-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    <p className="text-muted-foreground">
                        Questions? Contact us at{' '}
                        <a href="mailto:Flywithshelly@gmail.com" className="text-primary hover:underline">
                            Flywithshelly@gmail.com
                        </a>
                    </p>
                </motion.div>
            </div>
        </div>
    );
}
